import { Alert } from '@mui/material';
import QRCode from 'qrcode.react';
import React from 'react';
import { useForm } from 'react-hook-form';

import Arranger from '../../base/arranger';
import Button from '../../base/button';
import Modal from '../../base/modal';
import Spacer, { spc } from '../../base/spacer';
import Text from '../../base/text';
import TextField from '../../base/text-field';

import yup from '~/client/helpers/yup-extended';
import {
  muiError,
  useYupValidationResolver,
} from '~/client/hooks/yup-validation-resolver';

interface Setup2faModalProps {
  generateResp: { url: string; textCode: string } | undefined;
  open: boolean;
  handleClose: () => void;
  enableOTPHandler: (otpToken: number) => Promise<any>;
  isVerifyTotp: boolean;
  isEnforced?: boolean;
  enforcementMessage?: string;
}
interface InputData {
  otp: string;
}
const validationSchema = yup.object().shape({
  otp: yup
    .string()
    .required('OTP is required')
    .matches(/^\d{6}$/, 'OTP must be exactly 6 digits and numbers'),
});

const Setup2faModal = ({
  generateResp,
  open,
  handleClose,
  enableOTPHandler,
  isVerifyTotp,
  isEnforced = false,
  enforcementMessage,
}: Setup2faModalProps) => {
  const resolver = useYupValidationResolver(validationSchema);
  const { register, handleSubmit, formState, setError, setValue } =
    useForm<InputData>({
      mode: 'onChange',
      resolver,
    });

  const onSubmit = async (input: InputData) => {
    await enableOTPHandler(parseInt(input.otp, 10)).then((resp: any) => {
      if (resp && 'error' in resp) {
        setError('otp', {
          message:
            resp?.error?.data?.error ??
            'Verification failed. Please try again.',
        });
      }
    });
  };

  return (
    <Modal
      open={open}
      onClose={isEnforced ? () => {} : handleClose}
      title="Set up Two-factor Authentication"
      style={{ width: '60%', marginLeft: '20%' }}
      hideCloseButton={isEnforced}
    >
      {isEnforced && enforcementMessage && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {enforcementMessage}
          </Alert>
          <Spacer mb={spc.m} />
        </>
      )}
      <Text>
        To enable two-factor authentication, scan the QR code with your
        authenticator app or enter the manual code below.
      </Text>
      <Spacer mb={spc.l} />

      <Spacer mb={spc.m} />
      <Arranger
        alignContent="center"
        justify="center"
        alignItems="center"
        direction="column"
      >
        <QRCode value={generateResp?.url ?? ''} size={228} />
        <Spacer mb={spc.m} />
        <Text sz="title">Manual Code: {generateResp?.textCode} </Text>
      </Arranger>

      <Spacer mb={spc.m} />

      <Spacer mb={spc.m} />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('otp', {
            onChange: (e) => {
              const filtered = e.target.value.replace(/\D/g, '').slice(0, 6);
              setValue('otp', filtered, { shouldValidate: true });
            },
          })}
          {...muiError(formState.errors, 'otp')}
          label="6-digit verification Code."
          autoFocus
          required
          fullWidth
          inputProps={{
            style: { textAlign: 'center', fontSize: 30 },
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 6,
          }}
        />
        <Spacer mb={spc.m} />
        <Button
          type="submit"
          disabled={!formState.isValid}
          loading={isVerifyTotp}
          fullWidth
        >
          Enable two-factor authentication
        </Button>
      </form>
    </Modal>
  );
};

export default Setup2faModal;
